import React, { useEffect } from 'react';
import { useCSVStore, DateRange } from '../../stores/csvStore';
import { detectCSVStructure } from '../../utils/csvStructureDetector';
import './DateRangeSelector.css';

const DateRangeSelector: React.FC = () => {
  const { 
    isMultiPeriodMode,
    detectedDateRange,
    setDetectedDateRange,
    accessibleLocations,
    isMultiLocationMode,
    csvData,
    uploadedFile
  } = useCSVStore();

  // Detect date range from uploaded CSV data
  useEffect(() => {
    if (isMultiPeriodMode && csvData && csvData.length > 1) {
      try {
        const structure = detectCSVStructure(csvData);
        if (structure.isMultiPeriod) {
          // Extract actual date ranges from the CSV data
          const dataRows = csvData.slice(1); // Skip header
          const years: number[] = [];
          const months: number[] = [];

          dataRows.forEach(row => {
            if (structure.yearColumnIndex >= 0 && structure.monthColumnIndex >= 0) {
              const year = parseInt(row[structure.yearColumnIndex]);
              const month = parseInt(row[structure.monthColumnIndex]);
              
              if (!isNaN(year) && year >= 1900 && year <= 3000) {
                years.push(year);
              }
              if (!isNaN(month) && month >= 1 && month <= 12) {
                months.push(month);
              }
            }
          });

          if (years.length > 0 && months.length > 0) {
            const minYear = Math.min(...years);
            const maxYear = Math.max(...years);
            const minMonth = Math.min(...months.filter((_, index) => years[index] === minYear));
            const maxMonth = Math.max(...months.filter((_, index) => years[index] === maxYear));

            const detectedRange: DateRange = {
              startYear: minYear,
              startMonth: minMonth,
              endYear: maxYear,
              endMonth: maxMonth
            };

            setDetectedDateRange(detectedRange);
          }
        }
      } catch (error) {
        console.error('Error detecting date range:', error);
      }
    } else if (!isMultiPeriodMode) {
      setDetectedDateRange(null);
    }
  }, [isMultiPeriodMode, csvData, setDetectedDateRange]);

  // Calculate statistics based on detected date range
  const calculateStats = () => {
    if (!detectedDateRange) return null;

    const monthCount = calculateMonthCount(detectedDateRange);
    const locationCount = isMultiLocationMode ? accessibleLocations.length : 1;
    const totalSubmissions = monthCount * locationCount;

    return {
      monthCount,
      locationCount,
      totalSubmissions
    };
  };

  // Calculate month count from date range
  const calculateMonthCount = (dateRange: DateRange): number => {
    const startDate = new Date(dateRange.startYear, dateRange.startMonth - 1, 1);
    const endDate = new Date(dateRange.endYear, dateRange.endMonth - 1, 1);
    
    let months = 0;
    let currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      months++;
      currentDate.setMonth(currentDate.getMonth() + 1);
    }
    
    return months;
  };

  // Format month name
  const getMonthName = (month: number): string => {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return monthNames[month - 1] || month.toString();
  };

  if (!isMultiPeriodMode) {
    return null;
  }

  const stats = calculateStats();

  return (
    <div className="date-range-selector">
      <h3>📅 Detected Date Range</h3>
      
      {!detectedDateRange && !uploadedFile && (
        <div className="no-data-message">
          <p>📤 Upload a CSV file to automatically detect the date range</p>
        </div>
      )}

      {!detectedDateRange && uploadedFile && (
        <div className="detection-error">
          <p>⚠️ Could not detect date range from CSV file</p>
          <p>Please ensure your CSV contains "Year" and "Month" columns with valid data</p>
        </div>
      )}

      {detectedDateRange && (
        <>
          {/* Detected Date Range Display */}
          <div className="detected-range">
            <div className="range-display">
              <div className="range-item">
                <span className="range-label">From:</span>
                <span className="range-value">
                  {getMonthName(detectedDateRange.startMonth)} {detectedDateRange.startYear}
                </span>
              </div>
              <div className="range-arrow">→</div>
              <div className="range-item">
                <span className="range-label">To:</span>
                <span className="range-value">
                  {getMonthName(detectedDateRange.endMonth)} {detectedDateRange.endYear}
                </span>
              </div>
            </div>
          </div>

          {/* Statistics */}
          {stats && (
            <div className="date-summary">
              <h4>Submission Summary</h4>
              <div className="summary-grid">
                <div className="summary-item">
                  <span className="summary-label">Time Periods:</span>
                  <span className="summary-value">{stats.monthCount} months</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Locations:</span>
                  <span className="summary-value">
                    {stats.locationCount} 
                    {isMultiLocationMode ? ' (detected from CSV)' : ' (selected)'}
                  </span>
                </div>
                <div className="summary-item total">
                  <span className="summary-label">Total Submissions:</span>
                  <span className="summary-value">{stats.totalSubmissions}</span>
                </div>
              </div>

              {stats.totalSubmissions > 50 && (
                <div className="performance-warning">
                  📊 Large dataset: {stats.totalSubmissions} submissions will be processed. 
                  This may take 3-5 minutes to complete.
                </div>
              )}

              {stats.totalSubmissions > 200 && (
                <div className="performance-warning severe">
                  ⚠️ Very large dataset: Processing {stats.totalSubmissions} submissions may take significant time.
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* CSV Format Reminder */}
      <div className="format-reminder">
        <h5>✅ Automatic Detection:</h5>
        <ul>
          <li>Date range is automatically detected from your CSV data</li>
          <li>Ensure <strong>"Year"</strong> column contains 4-digit years (e.g., 2023)</li>
          <li>Ensure <strong>"Month"</strong> column contains month numbers (1-12)</li>
          {isMultiLocationMode && (
            <li>Ensure <strong>"Location"</strong> or <strong>"Dealer"</strong> column is present</li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default DateRangeSelector;
